import React from 'react'
import Link from '@mui/material/Link'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { useTranslation } from 'react-i18next'
import AddField from '@/components/addField/AddField'
import ActionButton from '@/components/buttons/ActionButton'
import SendIcon from '@mui/icons-material/Send'
import { useNavigate } from 'react-router-dom'
import { useLazyChangePasswordQuery } from '@/store/apis/auth'
import { useSearchParams } from 'react-router-dom'

export const verifyPasswords = (
  password1: string,
  password2: string,
): boolean => {
  const minLength = 8
  return (
    !!(password1 && password2) &&
    password1 === password2 &&
    password1.length >= minLength
  )
}

const ChangePassword: React.FC = () => {
  const [changePasswordQuery, { isSuccess, isError, isFetching }] =
    useLazyChangePasswordQuery()
  const { t } = useTranslation('default')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password1, setPassword1] = React.useState<string>('')
  const [password2, setPassword2] = React.useState<string>('')
  const token = searchParams.get('token')

  const changePassword = async (event?: React.SyntheticEvent) => {
    event && event.preventDefault()
    if (password1 === password2 && token) {
      await changePasswordQuery({ password: password1, token })
    }
  }

  let description: string | React.ReactNode = t(
    'login.changePasswordDescription',
  )
  if (!token || isError) {
    description = (
      <b>
        <i>{t('login.noValidToken')}</i>
      </b>
    )
  } else if (isSuccess) {
    description = t('login.passwordChanged')
  }

  return (
    <form onSubmit={changePassword}>
      <Grid
        container
        direction="column"
        justifyContent="space-between"
        alignItems="center"
        spacing={3}
      >
        <Grid item>
          <Typography component="h1">{description}</Typography>
        </Grid>
        {!isSuccess && (
          <>
            <Grid item>
              <List sx={{ listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item' }}>
                  {t('login.passwordReq.min8')}
                </ListItem>
              </List>
            </Grid>
            <Grid item>
              <AddField
                label={t('login.password')}
                type="password"
                value={password1}
                onChange={(newValue) => setPassword1(newValue)}
              />
            </Grid>
            <Grid item>
              <AddField
                label={t('login.repeatPassword')}
                type="password"
                value={password2}
                onChange={(newValue) => setPassword2(newValue)}
              />
            </Grid>
            <Grid item>
              <ActionButton
                icon={<SendIcon />}
                isLoading={isFetching}
                disabled={!verifyPasswords(password1, password2)}
                label={t('login.changePassword')}
                onClick={changePassword}
                type="submit"
              />
            </Grid>
          </>
        )}
        <Grid item>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/')}
          >
            {t('login.backToLogin')}
          </Link>
        </Grid>
      </Grid>
    </form>
  )
}

export default ChangePassword
